import { useMemo, useState } from "react";
import { useGetList, useNotify, useRefresh, useUpdate } from "ra-core";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Ban, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import { useHasPermission } from "../../rbac";

type CompanyRow = {
  id: number;
  name: string;
  credit_limit: number | null;
  outstanding_balance: number | null;
  payment_terms_days: number | null;
};

const fmtINR = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const standingOf = (
  limit: number,
  outstanding: number,
): "Healthy" | "Nearing" | "Breach" => {
  if (limit <= 0) return "Healthy";
  const pct = outstanding / limit;
  if (pct >= 1) return "Breach";
  if (pct >= 0.85) return "Nearing";
  return "Healthy";
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`block text-sm font-medium leading-none ${className ?? ""}`}>
    {children}
  </label>
);

export const CreditLimitsPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const canEdit = useHasPermission("manage_credit_limits");
  const [search, setSearch] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [targetId, setTargetId] = useState<number | null>(null);
  const [orderValue, setOrderValue] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [editLimit, setEditLimit] = useState("");
  const [editTerms, setEditTerms] = useState("");
  const [update] = useUpdate();

  const { data, isLoading } = useGetList<CompanyRow>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search ? row.name.toLowerCase().includes(search.toLowerCase()) : true,
      ),
    [data, search],
  );

  const activeRecord = useMemo(
    () => rows.find((r) => r.id === targetId) ?? rows[0] ?? null,
    [rows, targetId],
  );

  const attemptedOrder = orderValue ? parseFloat(orderValue) : 0;
  const projectedUtil =
    Number(activeRecord?.outstanding_balance ?? 0) + attemptedOrder;
  const isBlocked = activeRecord
    ? projectedUtil > Number(activeRecord.credit_limit ?? 0)
    : false;

  const startAuthCheck = (row?: CompanyRow) => {
    setTargetId(row?.id ?? rows[0]?.id ?? null);
    setOrderValue("");
    setIsVerified(false);
    setAuthOpen(true);
  };

  const startEdit = (row: CompanyRow) => {
    setTargetId(row.id);
    setEditLimit(String(row.credit_limit ?? 0));
    setEditTerms(String(row.payment_terms_days ?? 30));
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!targetId) return;
    try {
      await update(
        "companies",
        {
          id: targetId,
          data: {
            credit_limit: Number(editLimit),
            payment_terms_days: Number(editTerms),
          },
          previousData: { id: targetId },
        },
        { returnPromise: true },
      );
      notify("Credit limit updated.", { type: "success" });
      setEditOpen(false);
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    }
  };

  return (
    <div className="p-6 bg-slate-50/30 min-h-screen">
      <PageHeader
        title="Credit Limit Enforcement"
        description="Manage customer credit ceilings and validate proposed orders against live exposure."
      />

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold text-slate-900">
            Corporate Risk Registry
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Account filter..."
                className="h-8 pl-8 border-slate-300 text-xs shadow-none w-[160px]"
              />
            </div>
            <Button
              onClick={() => startAuthCheck()}
              className="bg-slate-900 hover:bg-slate-800 font-bold text-white text-xs h-8 shadow-md gap-1.5"
            >
              <ShieldAlert className="h-3.5 w-3.5" /> Test Order Constraint
            </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Customer Account
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Assigned Limit
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Outstanding
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Utilization
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">
                  Standing
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    Loading credit registry…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    No customers have a credit limit set yet.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const limit = Number(row.credit_limit ?? 0);
                const outstanding = Number(row.outstanding_balance ?? 0);
                const pct = limit > 0 ? (outstanding / limit) * 100 : 0;
                const state = standingOf(limit, outstanding);
                return (
                  <tr
                    key={row.id}
                    className="even:bg-slate-50/50 hover:bg-slate-100/50 border-b border-slate-200 transition-colors"
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-bold text-slate-900 align-middle">
                      <div className="flex items-center gap-2">
                        {row.name}
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] text-slate-500"
                            onClick={() => startEdit(row)}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-black text-slate-900 align-middle">
                      {fmtINR(limit)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-black text-slate-900 align-middle">
                      {fmtINR(outstanding)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-full max-w-[200px] bg-slate-100 border rounded-full h-2 overflow-hidden shadow-inner">
                          <div
                            className={`h-full transition-all duration-500 ${state === "Breach" ? "bg-red-600" : state === "Nearing" ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span
                          className={`text-xs font-black ${state === "Breach" ? "text-red-600" : "text-slate-600"}`}
                        >
                          {Math.round(pct)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Badge
                        className={`${
                          state === "Healthy"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                            : state === "Breach"
                              ? "bg-red-100 text-red-800 border-red-200"
                              : "bg-amber-100 text-amber-800 border-amber-200"
                        } font-bold border shadow-none`}
                      >
                        {state === "Breach" ? "BLOCK ACTIVE" : state}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Order authorization check */}
      <Sheet open={authOpen} onOpenChange={setAuthOpen}>
        <SheetContent className="sm:max-w-[460px] p-0 flex flex-col bg-white border-l border-slate-200 h-full">
          <SheetHeader className="p-6 bg-slate-900 text-white">
            <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-400" /> Test Order Constraint
            </SheetTitle>
            <SheetDescription className="text-slate-400 font-medium mt-1">
              Check a new inbound sales order against the customer's live exposure.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-1.5">
              <Label className="text-slate-600 font-bold text-sm">Customer</Label>
              <select
                value={targetId ?? ""}
                onChange={(e) => {
                  setTargetId(e.target.value ? Number(e.target.value) : null);
                  setIsVerified(false);
                }}
                className="w-full h-11 px-3 border border-slate-300 bg-white rounded-md font-bold text-slate-900"
              >
                <option value="">— Select a customer —</option>
                {rows.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {activeRecord && (
              <div className="bg-slate-50 p-4 rounded-xl border space-y-2 shadow-sm text-sm font-medium text-slate-600">
                <div className="flex justify-between">
                  <span>Authorized Limit:</span>
                  <span className="font-bold text-slate-900">
                    {fmtINR(activeRecord.credit_limit)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span>Current Exposure:</span>
                  <span className="font-bold text-slate-900">
                    {fmtINR(activeRecord.outstanding_balance)}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-slate-600 font-bold text-sm">
                Proposed Order Value
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">
                  ₹
                </span>
                <Input
                  type="number"
                  value={orderValue}
                  onChange={(e) => {
                    setOrderValue(e.target.value);
                    setIsVerified(false);
                  }}
                  placeholder="0.00"
                  className="h-12 pl-9 border-slate-300 text-xl font-black tracking-tight"
                />
              </div>
            </div>

            {!isVerified && (
              <Button
                onClick={() => attemptedOrder > 0 && setIsVerified(true)}
                disabled={attemptedOrder <= 0}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold text-white shadow-lg gap-2 animate-in fade-in"
              >
                <ShieldCheck className="w-4 h-4" /> Run Validation Check
              </Button>
            )}

            {isVerified && attemptedOrder > 0 && activeRecord && (
              <div
                className={`animate-in slide-in-from-bottom-4 duration-300 p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-3 text-center ${isBlocked ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}
              >
                {isBlocked ? (
                  <>
                    <div className="h-14 w-14 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 mb-1">
                      <Ban className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-black text-rose-700 uppercase tracking-wide">
                      ORDER DENIED
                    </h3>
                    <p className="text-sm font-bold text-rose-900">
                      Attempt violates authorized ceiling.
                      <br />
                      Exceeds allowance by{" "}
                      <span className="underline">
                        {fmtINR(
                          projectedUtil - Number(activeRecord.credit_limit ?? 0),
                        )}
                      </span>
                      .
                    </p>
                  </>
                ) : (
                  <>
                    <div className="h-14 w-14 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mb-1">
                      <ShieldCheck className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-black text-emerald-700 uppercase tracking-wide">
                      ORDER APPROVED
                    </h3>
                    <p className="text-sm font-bold text-emerald-900">
                      Sufficient credit cover.
                      <br />
                      New utilization{" "}
                      {Math.round(
                        (projectedUtil / Number(activeRecord.credit_limit ?? 1)) *
                          100,
                      )}
                      %.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          <SheetFooter className="p-6 bg-slate-50 border-t">
            <Button
              onClick={() => setAuthOpen(false)}
              variant="outline"
              className="w-full h-12 border-slate-300 font-bold text-slate-600"
            >
              Close Panel
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit credit limit */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="sm:max-w-[420px] p-0 flex flex-col bg-white border-l border-slate-200 h-full">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Edit credit limit</SheetTitle>
            <SheetDescription>
              {activeRecord?.name ?? ""}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Credit limit (₹)</Label>
              <Input
                type="number"
                value={editLimit}
                onChange={(e) => setEditLimit(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Payment terms (days)</Label>
              <Input
                type="number"
                value={editTerms}
                onChange={(e) => setEditTerms(e.target.value)}
              />
            </div>
          </div>
          <SheetFooter className="p-6 border-t flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={submitEdit}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};
